/**
 * WhatsAppStatusDot
 * Tiny traffic-light dot that polls /api/whatsapp/health every 60 s.
 * Shows a tooltip on hover with full detail.
 * Clicking opens a mini panel with live stats + auto-reconnect button.
 */
import React, { useEffect, useRef, useState } from "react";

interface WAHealth {
  status: "green" | "yellow" | "red";
  color: string;
  label: string;
  detail?: string;
  conversations?: number;
  active?: number;
  last_webhook?: number | null;
  webhook_age_s?: number | null;
  ultramsg?: Record<string, string>;
}

const POLL_INTERVAL_MS = 60_000;   // every 60 seconds
const RECONNECT_INTERVAL_MS = 15_000; // re-check 15 s after user hits reconnect

function fmtAge(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  if (seconds < 60)   return `${Math.round(seconds)}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  return `${Math.round(seconds / 3600)}h ago`;
}

const WhatsAppStatusDot: React.FC = () => {
  const [health, setHealth]     = useState<WAHealth | null>(null);
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const panelRef                = useRef<HTMLDivElement>(null);

  const fetchHealth = async () => {
    try {
      const r = await fetch("/api/whatsapp/health");
      if (r.ok) setHealth(await r.json());
    } catch {
      setHealth({ status: "red", color: "#ef4444", label: "Unreachable" });
    }
  };

  // Initial fetch + periodic poll
  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Close panel on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleReconnect = async () => {
    setLoading(true);
    // Trigger a fresh health check after a short delay (gives UltraMsg time to reconnect)
    setTimeout(async () => {
      await fetchHealth();
      setLoading(false);
    }, RECONNECT_INTERVAL_MS);
  };

  const color   = health?.color  ?? "#6b7280";
  const label   = health?.label  ?? "Checking…";
  const status  = health?.status ?? "yellow";

  const pulseClass = status === "green"  ? "wa-dot-pulse-green"
                   : status === "yellow" ? "wa-dot-pulse-yellow"
                   : "wa-dot-pulse-red";

  return (
    <div className="wa-status-wrap" ref={panelRef}>
      {/* ── Trigger dot ── */}
      <button
        className="wa-status-btn"
        onClick={() => setOpen(o => !o)}
        title={`WhatsApp: ${label}`}
        aria-label="WhatsApp service status"
      >
        <span className={`wa-dot ${pulseClass}`} style={{ background: color }} />
        <span className="wa-dot-label">WA</span>
      </button>

      {/* ── Detail panel ── */}
      {open && (
        <div className="wa-panel">
          <div className="wa-panel-header">
            <span className="wa-panel-icon">💬</span>
            <span className="wa-panel-title">WhatsApp Agent</span>
            <button className="wa-panel-close" onClick={() => setOpen(false)}>✕</button>
          </div>

          <div className="wa-panel-row">
            <span>Status</span>
            <span style={{ color, fontWeight: 700 }}>{label}</span>
          </div>

          {health?.ultramsg?.substatus && (
            <div className="wa-panel-row">
              <span>UltraMsg</span>
              <span>{health.ultramsg.substatus}</span>
            </div>
          )}

          {health?.ultramsg?.error && (
            <div className="wa-panel-row wa-panel-error">
              <span>Error</span>
              <span>{health.ultramsg.error}</span>
            </div>
          )}

          <div className="wa-panel-row">
            <span>Last message</span>
            <span>{fmtAge(health?.webhook_age_s)}</span>
          </div>

          <div className="wa-panel-row">
            <span>Conversations</span>
            <span>{health?.conversations ?? "—"} total · {health?.active ?? "—"} active</span>
          </div>

          {health?.detail && (
            <div className="wa-panel-detail">{health.detail}</div>
          )}

          <div className="wa-panel-actions">
            <button
              className="wa-panel-refresh"
              onClick={fetchHealth}
              title="Refresh status now"
            >
              🔄 Refresh
            </button>
            {status !== "green" && (
              <button
                className="wa-panel-reconnect"
                onClick={handleReconnect}
                disabled={loading}
                title="Attempt auto-reconnect"
              >
                {loading ? "⏳ Reconnecting…" : "⚡ Auto-reconnect"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppStatusDot;
